import { memo } from 'react';
import { useIntl } from 'react-intl';
import { Filtration } from '@library';
import {
    getCcmFilterValues,
    ccmValuesFromRange,
} from '../../util/getCcmFilterValues';
import useVehicleFilters from '../../queries/vehicles/useVehicleFilters';
import stripTypenameField from '../../../core/util/stripTypenameField';
import LicensePlateSearch from './LicensePlateSearch';

export interface MotorcycleSearchFiltersValue {
    make?: string;
    model?: string;
    ccm?: string;
    ratedPower?: string;
    modelYear?: string;
    type?: string;
    typeApprovalList: string[];
    search: string;
    licensePlate: string;
}

export const filtersInitialValue: MotorcycleSearchFiltersValue = {
    make: undefined,
    model: undefined,
    ccm: undefined,
    ratedPower: undefined,
    modelYear: undefined,
    type: undefined,
    typeApprovalList: [],
    search: '',
    licensePlate: '',
};

const MotorcycleSearchFilters = ({
    filters,
    onChangeFilters,
    shortSearch,
}: {
    filters: MotorcycleSearchFiltersValue;
    onChangeFilters: (value: MotorcycleSearchFiltersValue) => void;
    shortSearch?: boolean;
}) => {
    const intl = useIntl();

    const { vehicleFilters } = useVehicleFilters({
        search: filters.search,
        make: filters.make,
        type: filters.type,
        typeApprovalList: filters.typeApprovalList,
        model: filters.model,
        ...ccmValuesFromRange(filters.ccm),
    });

    const { ccmStart, ccmEnd, ...vehicleFiltersWithoutCcm } =
        stripTypenameField(vehicleFilters);

    const processedValidFilterValues = Object.fromEntries(
        Object.entries(vehicleFiltersWithoutCcm).map(([key, values]) => {
            return [
                key,
                values
                    .filter(value => value !== '')
                    .map((item: string) => ({
                        label: item.toUpperCase(),
                        value: `${item}`,
                    })),
            ];
        })
    );

    const processedCCmFilterValues = getCcmFilterValues(intl, ccmStart, ccmEnd);

    return (
        <Filtration
            filterValue={filters}
            filters={[
                {
                    field: 'search',
                    type: 'search',
                    placeholder: intl.formatMessage({
                        id: 'make, model, type-nr, type approval',
                    }),
                    styles: !shortSearch
                        ? {
                              maxWidth: 'min(400px, 100%)',
                              width: '100%',
                          }
                        : undefined,
                },

                {
                    field: 'make',
                    type: 'select',
                    placeholder: intl.formatMessage({
                        id: 'make',
                    }),
                    items: processedValidFilterValues.manufacturerName,
                    windowRender: { height: 225 },
                    searchable: true,
                },
                {
                    field: 'model',
                    type: 'select',
                    placeholder: intl.formatMessage({
                        id: 'model',
                    }),
                    items: processedValidFilterValues.model,
                    windowRender: { height: 225 },
                    searchable: true,
                },
                {
                    field: 'ccm',
                    type: 'select',
                    placeholder: intl.formatMessage({
                        id: 'ccm',
                    }),
                    items: processedCCmFilterValues,
                    windowRender: { height: 225 },
                    searchable: true,
                },
                {
                    field: 'type',
                    type: 'select',
                    placeholder: intl.formatMessage({
                        id: 'type',
                    }),
                    items: processedValidFilterValues.typeNumbers,
                    windowRender: { height: 225 },
                    searchable: true,
                },
                {
                    field: 'typeApprovalList',
                    type: 'multiSelect',
                    placeholder: intl.formatMessage({
                        id: 'typeApprovalList',
                    }),
                    items: processedValidFilterValues.typeAcceptanceList,
                    windowRender: { height: 225 },
                    searchable: true,
                },
                {
                    field: 'licensePlate',
                    type: 'custom',
                    component: (
                        <LicensePlateSearch
                            licensePlateNumber={filters.licensePlate}
                            setLicensePlateNumber={value => {
                                onChangeFilters({
                                    ...filters,
                                    licensePlate: value,
                                    typeApprovalList: [],
                                });
                            }}
                            onLicensePlateResultUpdate={typeApprovalList => {
                                onChangeFilters({
                                    ...filters,
                                    typeApprovalList: typeApprovalList ?? [],
                                });
                            }}
                        />
                    ),
                    styles: {
                        width: '190px',
                    },
                },
            ]}
            emptyValue={filtersInitialValue}
            onChangeFilters={onChangeFilters}
        />
    );
};

export default memo(MotorcycleSearchFilters);
